// @ts-check
// Corpo da nota ou da tarefa na página e no painel (doc 10, seção 6.6, widget FIELD_RICH_TEXT): o mesmo editor das
// notas, com menu "/" e menções, salvo sozinho 300 ms depois de parar de digitar (e na hora, ao sair da aba).
import { html } from 'htm/preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import { updateRecordOptimistically } from '../../data/record-store.js';
import { isFieldReadOnly } from '../../lib/field-metadata.js';
import { getActivityBodyField, getBodyText } from './activity-targets.js';
import { RichTextEditor } from './rich-text-editor.js';
import { t } from '../../i18n/index.js';

const PERSIST_DELAY_MILLISECONDS = 300;

/**
 * @param {{ objectMetadata: import('../../lib/field-metadata.js').ObjectMetadata, record: any, isReadOnly: boolean }} props
 */
export function ActivityBodyTab({ objectMetadata, record, isReadOnly }) {
  const bodyField = getActivityBodyField(objectMetadata);
  const [initialBody] = useState(() => (bodyField ? getBodyText(record[bodyField.name]) : ''));
  const [status, setStatus] = useState(/** @type {'idle' | 'saving' | 'saved'} */ ('idle'));
  const pendingRef = useRef(/** @type {{ timer: ReturnType<typeof setTimeout>, markdown: string } | null} */ (null));

  const persist = async (/** @type {string} */ markdown) => {
    pendingRef.current = null;
    if (!bodyField) return;
    setStatus('saving');
    try {
      await updateRecordOptimistically(objectMetadata, record.id, { [bodyField.name]: { markdown, blocknote: null } });
      setStatus('saved');
    } catch {
      setStatus('idle');
    }
  };

  useEffect(() => () => {
    const pending = pendingRef.current;
    if (pending) {
      clearTimeout(pending.timer);
      persist(pending.markdown);
    }
  }, []);

  if (!bodyField) return null;
  const readOnly = isReadOnly || isFieldReadOnly(bodyField);
  return html`<div class="activity-body-tab" data-testid="activity-body">
    <span class="note-editor-status" aria-live="polite">${status === 'saving' ? t('parity.notes.saving') : status === 'saved' ? t('parity.notes.saved') : ''}</span>
    <${RichTextEditor}
      value=${initialBody}
      readOnly=${readOnly}
      placeholder=${t('recordPage.body.placeholder')}
      onChange=${(/** @type {string} */ markdown) => {
        if (pendingRef.current) clearTimeout(pendingRef.current.timer);
        pendingRef.current = { markdown, timer: setTimeout(() => persist(markdown), PERSIST_DELAY_MILLISECONDS) };
      }}
    />
  </div>`;
}
